import { Button } from "components/DesignSystem/Button/Button";
import { MobileInput } from "components/DesignSystem/MobileInput/MobileInput";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { timezones } from "data/timezones";
import { useToast } from "hooks/useToast";
import type { User } from "types/Models/user";
import React, { useState } from "react";
import { Formik, Form, FormikValues } from "formik";
import { updateName } from "../../apis/profileData";
import { useUpdateAdditionalInfoMutation } from "store/apis/profile";
import { PROFILE_UPDATED } from "../../constants/userTypes";
import { profileUpdateSchema } from "../../formValidations/profileUpdateSchema";
import "../../static/styles/components/profileUpdate.css";

import "../../static/styles/components/profileUpdate.css";
import Pencil from "components/icons/pencil";
import { EmailUpdate } from "./EmailUpdate";
import { MobileUpdate } from "./MobileUpdate";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { EditPencil } from "components/icons/EditPencil";
import classNames from "classnames";

export const timezoneOptions = timezones
  .map(({ value, text, utc }) => ({
    value: JSON.stringify(utc),
    label: `${value} - ${text}`,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

type TeamUpdate = {
  linkedIn_url: string;
  secondary_mobile: string;
  current_timezone: string;
  slack_mention_id?: string;
};

type ProfileUpdateProps = {
  user: User;
  updateUser: (user: User) => void;
  isCustomerView?: boolean;
  updateUserProfile?: (user: TeamUpdate) => User;
};

export const ProfileUpdate = ({
  user,
  updateUser,
  isCustomerView = true,
  updateUserProfile,
}: ProfileUpdateProps) => {
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [showMobileModal, setShowMobileModal] = useState<boolean>(false);
  const [updateAdditionalInfo] = useUpdateAdditionalInfoMutation();
  const { successToast, alertToast } = useToast();

  const onSubmit = async (values: User | TeamUpdate) => {
    const firstNameChanged = user.first_name !== (values as User).first_name;
    const lastNameChanged = user.last_name !== (values as User).last_name;
    const secondaryMobile = values.secondary_mobile;

    const finalValues = {
      ...values,
      secondary_mobile: secondaryMobile,
    };

    try {
      if (firstNameChanged || lastNameChanged) {
        const updatedProfile: unknown = await updateName(finalValues as User);
        updateUser?.(updatedProfile as User);
      }

      let updatedProfile;

      if (updateUserProfile) {
        updatedProfile = updateUserProfile(finalValues as TeamUpdate);
      } else {
        updatedProfile = await updateAdditionalInfo(finalValues).unwrap();
      }

      updateUser?.({ data: { data: updatedProfile } } as unknown as User);

      successToast({ message: PROFILE_UPDATED });
    } catch (error: any) {
      alertToast({ message: error?.response?.data?.error?.message });
    }
  };

  const {
    email,
    first_name,
    last_name,
    mobile,
    linkedIn_url,
    current_timezone,
    secondary_mobile,
    calendar_url,
    slack_mention_id,
  } = user;

  const cpaInitialValues = {
    secondary_mobile: secondary_mobile ? `${secondary_mobile}` : "",
    current_timezone,
    linkedIn_url,
    slack_mention_id: slack_mention_id ?? "",
  };

  const customerInitialValues = {
    email,
    first_name,
    last_name,
    mobile: mobile,
    secondary_mobile: secondary_mobile ? `${secondary_mobile}` : "",
    current_timezone,
    linkedIn_url,
    calendar_url,
    slack_mention_id: slack_mention_id ?? "",
  };

  return (
    <div className="t-w-full">
      <Formik
        onSubmit={onSubmit}
        initialValues={
          isCustomerView ? customerInitialValues : cpaInitialValues
        }
        validationSchema={isCustomerView ? profileUpdateSchema : null}
        enableReinitialize
      >
        {({ values, dirty, isSubmitting, resetForm }: FormikValues) => {
          const selected = timezoneOptions.find((option) =>
            option.value.includes(values.current_timezone)
          );

          return (
            <Form className="all:unset t-flex t-flex-col t-gap-5">
              {isCustomerView && (
                <>
                  <div className="t-flex t-gap-3 sm:t-flex-col">
                    <TextInput label="First name" name="first_name" />
                    <TextInput label="Last name" name="last_name" />
                  </div>

                  <div className="t-grid t-grid-cols-2 t-gap-3 sm:t-grid-cols-1">
                    <div className="t-flex t-flex-col t-gap-1.5">
                      <div className="t-text-body-sm t-text-text-30">Email</div>
                      <div
                        className="t-flex t-gap-1 t-items-center hover:t-cursor-pointer"
                        onClick={() => setShowEmailModal(true)}
                      >
                        <div className="t-text-body-sm">{values?.email}</div>
                        <EditPencil className="t-text-subtext t-text-text-30" />
                      </div>
                    </div>
                    <div className="t-flex t-flex-col t-gap-1.5">
                      <div className="t-text-body-sm t-text-text-30">
                        Primary mobile number
                      </div>
                      <div
                        className="t-flex t-w-fit t-gap-1 t-items-center hover:t-cursor-pointer"
                        onClick={() => setShowMobileModal(true)}
                      >
                        <div className="t-text-body-sm">{`${values?.mobile}`}</div>
                        <EditPencil className="t-text-subtext t-text-text-30" />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="t-h-fit">
                <MobileInput
                  label="Secondary mobile number"
                  name="secondary_mobile"
                  value={values?.secondary_mobile}
                />
              </div>

              <div className="t-flex t-gap-3 sm:t-flex-col">
                <TextInput
                  label="Calendar booking link"
                  tooltipText="Add your calendar booking link so our team can schedule meeting with you when needed."
                  name="calendar_url"
                  placeholder="Your Calendar URL"
                  value={values?.calendar_url}
                />
                <TextInput
                  label="LinkedIn"
                  name="linkedIn_url"
                  placeholder="Your LinkedIn URL"
                  value={values?.linkedIn_url}
                />
              </div>

              <TextInput
                label="Slack mention ID"
                name="slack_mention_id"
                placeholder="e.g. U0123456789"
                value={values?.slack_mention_id}
              />

              <Combobox
                withForm
                label="Time zone"
                name="current_timezone"
                options={timezoneOptions}
                value={selected || null}
                placeholder="Select timezone"
              />
              <div
                className={classNames("t-flex t-justify-end", {
                  "t-hidden": !dirty,
                })}
              >
                <div className="t-flex t-gap-3">
                  <Button
                    size="regular"
                    disabled={!dirty || isSubmitting}
                    type="button"
                    customType="ghost"
                    onClick={() => resetForm()}
                  >
                    Cancel
                  </Button>
                  <Button
                    customType="primary"
                    size="regular"
                    disabled={!dirty || isSubmitting}
                    type="submit"
                    isLoading={isSubmitting}
                  >
                    Update
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
      <EmailUpdate
        email={email}
        showEmailModal={showEmailModal}
        setShowEmailModal={setShowEmailModal}
      />
      <MobileUpdate
        showMobileModal={showMobileModal}
        setShowMobileModal={setShowMobileModal}
        mobile={mobile}
      />
    </div>
  );
};
